// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import {
    ApiParameter,
    get,
    HasPagingParameters,
    patch,
    post,
    ServiceResult,
} from "@insite/client-framework/Services/ApiService";
import { PaginationModel, ProductLineModel } from "@insite/client-framework/Types/ApiModels";

const autoDeliveryUrl = "/api/guest/autodelivery";

export interface GetAutoDeliveryApiParameter extends ApiParameter {
    productId: string;
}
export interface CustomerAutoDeliveryDtoModel {
    billToId: string;
    productId: string;
    shipToId: string;
    carrierId: string;
    shipViaId: string;
    purchaseOrderNo: string;
    qty: number;
    uom: string;
    startDate: string;
    repetition: string;
    isCancelled: boolean;
    shipBranchName: string;
    nextDeliveryDate: string;
}

export interface GetAutoDeliveryCollectionApiParameter extends ApiParameter, HasPagingParameters {
    ongoing?: boolean;
    skipOrder?: string;
    cancelOrder?: string;
}
export interface AutoDeliveryItemModel extends ProductLineModel {
    shipToLabel: string;
    shipToName: string;
    productId: string;
    productErpNumber: string;
    purchaseOrderNo: string;
    scheduleDate: string;
    nextDeliverDateSkipped?: boolean;
    nextOrderDate: string;
    notificationPreference: string;
    notificationPreferences: any[];
    qty: number;
    uom: string;
    canAddToCart: boolean;
    smallImagePath: string;
    shortDescription: string;
    productDetailUrl: string;
}
export interface AutoDeliveryCollectionModel {
    autoDeliveryDtos: AutoDeliveryItemModel[];
    pagination: PaginationModel;
}
export interface GetAutoDeliveryModel {
    customerAutoDeliveryDto?: CustomerAutoDeliveryDtoModel;
    repeatsDto: string[];
    defaultPurchaseOrderNo: string;
}

export interface ShipViasModel {
    id: string;
    description: string;
    isDefault: boolean;
}

export interface CarriersDtoModel {
    id: string;
    description: string;
    shipVias: ShipViasModel[];
}

export interface GetCarriersModel {
    carrierDtos: CarriersDtoModel[];
}
export interface GetEstimateTotalsApiParameter extends ApiParameter {
    productId: string;
    qty: number;
    shipViaId?: string;
    uom: string;
}

export interface GetEstimateTotalsModel {
    orderGrandTotal: number;
    orderGrandTotalDisplay: string;
    orderSubTotal: number;
    orderSubTotalDisplay: string;
    shippingAndHandling: number;
    shippingAndHandlingDisplay: string;
    totalTaxDisplay: string;
    shippingCalculated: boolean;
    isQtyAdjusted: boolean;
    qty: number;
}
export interface CreateAutoDeliveryDtoModel {
    purchaseOrderNo: string;
    startDate: string;
    qty: number;
    uom: string;
    productId: string;
    shipToId: string;
    carrierId: string;
    shipViaId: string;
    repetition: string;
}
export interface CancelAutoDeliveryModel {
    productId: string;
}
export interface SkipNextAutoDeliveryModel {
    productId: string;
}

export interface UpdateNotificationPreferenceModel {
    productId: string;
    notificationPreference: string;
}

export async function getAutoDeliveryCollection(
    parameter: GetAutoDeliveryCollectionApiParameter,
): Promise<ServiceResult<AutoDeliveryCollectionModel>> {
    try {
        const allReorders = await get<AutoDeliveryCollectionModel>(
            `${autoDeliveryUrl}/getAutoDeliveryCollection/`,
            parameter,
        );
        allReorders.autoDeliveryDtos.forEach(order => {
            order.id = order.productId;
        });
        return {
            successful: true,
            result: allReorders,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: (error as any)?.errorJson?.message,
        };
    }
}

export async function getAutoDelivery(
    parameter: GetAutoDeliveryApiParameter,
): Promise<ServiceResult<GetAutoDeliveryModel>> {
    try {
        const autoDeliveryResult = await get<GetAutoDeliveryModel>(
            `${autoDeliveryUrl}/getAutoDelivery/${parameter.productId}`,
        );
        return {
            result: autoDeliveryResult,
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: (error as any)?.errorJson?.message,
        };
    }
}

export async function getAutoCarriers(): Promise<ServiceResult<GetCarriersModel>> {
    try {
        const autoCarriersResult = await get<GetCarriersModel>(`${autoDeliveryUrl}/getcarriers`);
        return {
            result: autoCarriersResult,
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: (error as any)?.errorJson?.message,
        };
    }
}

export async function getEstimateTotals(
    parameter: GetEstimateTotalsApiParameter,
): Promise<ServiceResult<GetEstimateTotalsModel>> {
    try {
        const estimateTotalsResult = await get<GetEstimateTotalsModel>(`${autoDeliveryUrl}/estimateTotals`, parameter);
        return {
            result: estimateTotalsResult,
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: (error as any)?.errorJson?.message,
        };
    }
}

export async function createAutoDelivery(
    parameter: CreateAutoDeliveryDtoModel,
): Promise<ServiceResult<CreateAutoDeliveryDtoModel>> {
    try {
        const createAutoDeliveryResult = await post<any>(`${autoDeliveryUrl}/createAutoDelivery`, parameter);
        return {
            result: createAutoDeliveryResult,
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: (error as any)?.errorJson?.message,
        };
    }
}

export async function updateAutoDelivery(
    parameter: CustomerAutoDeliveryDtoModel,
): Promise<ServiceResult<CustomerAutoDeliveryDtoModel>> {
    try {
        const updateAutoDeliveryResult = await patch<any>(`${autoDeliveryUrl}/updateAutoDelivery`, parameter);
        return {
            result: updateAutoDeliveryResult,
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: (error as any)?.errorJson?.message,
        };
    }
}

export async function cancelAutoDelivery(
    parameter: CancelAutoDeliveryModel,
): Promise<ServiceResult<CancelAutoDeliveryModel>> {
    try {
        const cancelAutoDeliveryResult = await patch<any>(
            `${autoDeliveryUrl}/CancelAutoDelivery/${parameter.productId}`,
            {},
        );
        return {
            result: cancelAutoDeliveryResult,
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: (error as any)?.errorJson?.message,
        };
    }
}

export async function skipNextOrder(
    parameter: SkipNextAutoDeliveryModel,
): Promise<ServiceResult<SkipNextAutoDeliveryModel>> {
    try {
        const skipNextAutoDeliveryResult = await patch<any>(`${autoDeliveryUrl}/updateAutoDelivery`, {
            productId: parameter.productId,
            skipNextOrder: true,
        });
        return {
            result: skipNextAutoDeliveryResult,
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: (error as any)?.errorJson?.message,
        };
    }
}

export async function updateNotificationPreference(
    parameter: UpdateNotificationPreferenceModel,
): Promise<ServiceResult<UpdateNotificationPreferenceModel>> {
    try {
        const updateNotificationPreference = await patch<any>(`${autoDeliveryUrl}/updateAutoDelivery`, {
            productId: parameter.productId,
            notificationPreference: parameter.notificationPreference,
            updateNotificationPreference: true,
        });
        return {
            result: updateNotificationPreference,
            successful: true,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: (error as any)?.errorJson?.message,
        };
    }
}
